import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Helmet } from "react-helmet-async";
const AWSCloudSolutions = () => {
  const phrases = [
    "Multi-Region Landing Zones",
    "Serverless Data Pipelines",
    "Auto-Scaling EKS Clusters"
  ];

  const cardData = [
    { type: 'aws-core', content: '🧡' },
    { type: 'amber-wave', content: '📊' },
    { type: 'eks-stack', content: '⚙️' },
    { type: 'lambda-spark', content: '⚡' }
  ];

  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [cardIndex, setCardIndex] = useState(0);

  useEffect(() => {
    const currentPhrase = phrases[currentPhraseIndex];
    let timer;

    const handleTyping = () => {
      if (!isDeleting) {
        setDisplayedText(currentPhrase.substring(0, displayedText.length + 1));
        if (displayedText === currentPhrase) {
          timer = setTimeout(() => setIsDeleting(true), 1600);
          return;
        }
      } else {
        setDisplayedText(currentPhrase.substring(0, displayedText.length - 1));
        if (displayedText === '') {
          setIsDeleting(false);
          setCurrentPhraseIndex((prev) => (prev + 1) % phrases.length);
          return;
        }
      }

      const speed = isDeleting ? 30 : 80;
      timer = setTimeout(handleTyping, speed);
    };

    timer = setTimeout(handleTyping, 100);
    return () => clearTimeout(timer);
  }, [displayedText, isDeleting, currentPhraseIndex]);

  useEffect(() => {
    const cardTimer = setInterval(() => {
      setCardIndex((prev) => (prev + 1) % cardData.length);
    }, 1100);
    return () => clearInterval(cardTimer);
  }, []);

  // Intersection Observer for Scroll Animations
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.15
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-visible');
        }
      });
    }, observerOptions);

    const animatedElements = document.querySelectorAll(
      '.scroll-anim-fade-in, .scroll-anim-fade-in-up, .scroll-anim-fade-in-left, .scroll-anim-fade-in-right, .scroll-anim-flip, .scroll-anim-zoom-in'
    );

    animatedElements.forEach((el) => observer.observe(el));

    return () => {
      animatedElements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  // Content Data
  const coreServices = [
    { icon: "🧡", title: "Enterprise AWS Landing Zones & IAM", desc: "Design and scale resilient multi-account AWS topologies using Control Tower, structured Organizations, and zero-trust IAM permission guardrails." },
    { icon: "⚡", title: "Serverless Application Engineering", desc: "Build highly scalable, event-driven backend architectures leveraging AWS Lambda, API Gateway, Amazon EventBridge, and DynamoDB storage engines." },
    { icon: "⚙️", title: "Containerized Microservices & EKS", desc: "Deploy robust application workloads utilizing Amazon Elastic Kubernetes Service (EKS) or ECS Fargate with automated vertical scaling rules." },
    { icon: "📊", title: "High-Throughput Analytics & Data Lakes", desc: "Ingest, catalog, and query petabyte-scale unstructured records natively via Amazon S3, AWS Glue crawler pipelines, and Amazon Athena." },
    { icon: "🔗", title: "Infrastructure as Code (IaC) Synthesis", desc: "Establish declarative, repeatable environmental provisions using version-controlled Terraform modules or AWS CloudFormation templates." },
    { icon: "🛡️", title: "Network Security & Edge Mitigation", desc: "Enforce advanced network segregation using secure Amazon VPC setups, Transit Gateways, AWS WAF rules, and CloudFront caching parameters." },
    { icon: "🔄", title: "Continuous GitOps CI/CD Integration", desc: "Automate delivery lifecycles via secure GitHub Actions or AWS CodePipeline nodes, featuring integrated image registry validation checks." }
  ];

  const advancedServices = [
    { icon: "🔍", title: "Generative AI Integration & Bedrock", desc: "Securely build and scale internal applications using foundation models with retrieval-augmented generation (RAG) loops inside AWS Bedrock." },
    { icon: "💼", title: "Aurora Global Relational Tuning", desc: "Optimize globally distributed PostgreSQL or MySQL databases utilizing Amazon Aurora multi-master clusters for microsecond lag tolerances." },
    { icon: "🗃️", title: "DevSecOps Automated Compliance Gates", desc: "Implement active static code scanning and environmental validation using AWS Security Hub, Config rules, and Inspector utilities." },
    { icon: "⚖️", title: "FinOps Infrastructure Cost Optimization", desc: "Analyze runtime performance and lower billing totals via compute savings plans, Graviton instance migrations, and lifecycle storage policies." },
    { icon: "🚨", title: "Real-Time OpenSearch Log Metrics", desc: "Centralize infrastructure telemetry streams utilizing Amazon OpenSearch Services and CloudWatch metrics to resolve issues before drop-offs." },
    { icon: "📝", title: "Hybrid Systems & AWS Outposts Coordination", desc: "Extend native AWS cloud management APIs and container orchestration capabilities onto isolated on-premise physical rack setups." },
    { icon: "🌐", title: "Distributed Message Streaming via MSK", desc: "Orchestrate real-time, event-sourced interaction patterns using managed Apache Kafka (Amazon MSK) processing arrays." },
    { icon: "🧪", title: "Chaos Engineering & Resiliency Fault Testing", desc: "Validate platform infrastructure behavior under pressure using simulated network splits via AWS Fault Injection Service nodes." },
    { icon: "📦", title: "Automated Multi-Region Disaster Recovery", desc: "Construct low recovery-time-objective (RTO) blueprints featuring cross-region active replication and instant DNS route failovers." }
  ];

  const technologies = {
    "Compute & Containers": ["Amazon Elastic Kubernetes Service (EKS)", "AWS Lambda Serverless Compute", "Amazon ECS Fargate Containers", "Amazon EC2 Graviton Instances"],
    "Storage & Databases": ["Amazon Aurora (PostgreSQL / MySQL)", "Amazon DynamoDB NoSQL Key-Value", "Amazon S3 Intelligent Tiering", "Amazon ElastiCache Redis Clusters"],
    "Data & AI Analytics": ["Amazon Bedrock Generative AI Hub", "AWS Glue Data Catalogs & ETL", "Amazon Athena Serverless Analytics", "Amazon OpenSearch Log Monitoring"],
    "Networking & Governance": ["Amazon VPC & Transit Gateways", "AWS IAM Identity Center Blocks", "AWS Control Tower Landing Zones", "Amazon CloudFront Global CDN Edge"],
    "IaC & Deployment Pipelines": ["HashiCorp Terraform IaC Scripting", "AWS CloudFormation Templates", "AWS CodePipeline CI/CD Engines", "GitHub Actions GitOps Workflows"]
  };

  const processSteps = [
    { num: "01", title: "Infrastructure & Workload Assessment", desc: "Auditing monolithic computing items, evaluating container fitness, and evaluating legacy network security footprints." },
    { num: "02", title: "Target Cloud Schema Blueprinting", desc: "Structuring secure VPC subnets, defining multi-account landing setups, and designing data streaming lines." },
    { num: "03", title: "Infrastructure as Code Synthesis", desc: "Writing modular Terraform scripts, building custom Docker base files, and establishing initial repository configurations." },
    { num: "04", title: "Data Migration & Streaming Hubs", desc: "Moving structural databases via AWS DMS, establishing S3 buckets, and testing dynamic replication pipelines." },
    { num: "05", title: "Identity Governance & IAM Audits", desc: "Enforcing minimum-privilege boundaries, validating network routing rules, and testing high-load application triggers." },
    { num: "06", title: "Automated GitOps Cutover Push", desc: "Running absolute container orchestration test passes and shifting live domain targets over safely through DNS failover rules." },
    { num: "07", title: "Telemetry Analytics & FinOps Review", desc: "Reviewing metrics via CloudWatch, adjusting horizontal pod rules, and removing low-efficiency compute instances." }
  ];

  const industries = [
    "High-Velocity Financial Banking & FinTech Solutions", "Global Supply Chain Logistics & Inventory Operations", "Cross-Border Specialized Healthcare & Patient Management",
    "High-Density Retail, E-Commerce & Distribution Hubs", "Aerospace, Heavy Automotive & Component Assembly", "High-Growth Enterprise Software-as-a-Service (SaaS)",
    "Commercial Real Estate & Structural Asset Management", "Public Sector Government Agency Cloud Transformations", "Energy Infrastructure, Utilities & Grid Allocations",
    "Global Maritime Shipping, Cargo & Transport Networks", "Media Networks & High-Scale Digital Content Delivery", "Higher Education Multi-Campus Student Lifecycle Systems"
  ];

  const designBenefits = [
    "Zero Inter-Service Operational Synchronization Drift", "Up to 60% Reduction in Microservice Latency Paths", "100% Encrypted, Isolated Cloud Network Infrastructure",
    "Guaranteed 99.99% Cloud Service Environment Availability", "Drastic Drop in Manual Configuration and Deployment Tasks", "Highly Scalable Containerized Microservices Implementations",
    "Seamless Cross-Region Relational Data Sync Pipelines", "Real-Time Infrastructure Performance Telemetry Ingestion"
  ];

  const whyChooseUs = [
    "Certified AWS Solutions & Cloud Infrastructure Architects", "Proven Scaling Track Record Across Massive Cloud Workloads", "Airtight Zero-Trust IAM Identity Center Governance",
    "Deep Competency in Complex Serverless Backend Optimization", "Platform-Agnostic Hybrid System Interfacing Competency", "Resilient High-Availability EKS Cluster Infrastructure Setups",
    "End-to-End Environment Transport & Lifespan CI/CD Governance", "Transparent Engineering Goals & Verifiable Sprint Testing Metrics", "High-Availability Platform Integration Infrastructure SLAs",
    "Experienced Terraform IaC & Container Management Experts", "Iterative Proof-of-Concept Target Environment Testing Sprints", "100% Production Environment Architecture Stability Assurances"
  ];

  return (

    <>

      <Helmet>
        {/* Primary SEO */}
        <title>
          AWS Cloud Solutions | AWS Consulting & Cloud Migration Services | Affsoft IT Solutions Pvt. Ltd.
        </title>

        <meta
          name="title"
          content="AWS Cloud Solutions | AWS Consulting & Cloud Migration Services | Affsoft IT Solutions Pvt. Ltd."
        />

        <meta
          name="description"
          content="Affsoft IT Solutions Pvt. Ltd. provides AWS Cloud Solutions, including AWS consulting, cloud migration, AWS infrastructure management, DevOps, serverless architecture, cloud security, managed AWS services, and scalable enterprise cloud solutions."
        />

        <meta
          name="keywords"
          content="AWS Cloud Solutions, Amazon Web Services, AWS Consulting, AWS Cloud Migration, AWS Infrastructure, AWS DevOps, AWS Managed Services, AWS Cloud Security, Serverless Architecture, AWS Lambda, Amazon EC2, Amazon S3, AWS Cloud Architecture, Cloud Infrastructure, Enterprise Cloud Solutions, AWS Implementation, Affsoft IT Solutions Pvt. Ltd."
        />

        <meta name="robots" content="index, follow" />
        <meta name="author" content="Affsoft IT Solutions Pvt. Ltd." />
        <meta name="language" content="English" />
        <meta name="theme-color" content="#0d5c53" />

        {/* Canonical */}
        <link
          rel="canonical"
          href="https://www.affsoft.com/aws-cloud-solutions"
        />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta
          property="og:site_name"
          content="Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          property="og:title"
          content="Professional AWS Cloud Solutions | Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          property="og:description"
          content="Scale your business with AWS Cloud Solutions from Affsoft IT Solutions Pvt. Ltd. We specialize in AWS consulting, cloud migration, DevOps, serverless applications, cloud security, and managed AWS services."
        />
        <meta
          property="og:url"
          content="https://www.affsoft.com/aws-cloud-solutions"
        />
        <meta
          property="og:image"
          content="https://www.affsoft.com/images/affsoft-logo.png"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="AWS Cloud Solutions | AWS Consulting & Cloud Migration Services | Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          name="twitter:description"
          content="Accelerate your digital transformation with AWS Cloud Solutions from Affsoft IT Solutions Pvt. Ltd. We provide AWS consulting, cloud migration, DevOps, infrastructure management, cloud security, and enterprise AWS services."
        />
        <meta
          name="twitter:image"
          content="https://www.affsoft.com/images/affsoft-logo.png"
        />
      </Helmet>
      <div className="aws-services-page bg-light" style={{ fontFamily: '"Inter", sans-serif', overflowX: 'hidden' }}>

        {/* ================= HERO SECTION ================= */}
        <div
          className="hero-container container-fluid d-flex align-items-center justify-content-center position-relative overflow-hidden"
          style={{
            backgroundImage: "url('./images/home.jpg')",
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            minHeight: '85vh',
            backgroundColor: '#111721' // Deep AWS charcoal fallback
          }}
        >
          {/* Background Grid Lines */}
          <div className="grid-overlay position-absolute top-0 start-0 w-100 h-100" style={{ opacity: 0.12 }}>
            <div className="grid-line line-v-1"></div>
            <div className="grid-line line-v-2"></div>
            <div className="grid-line line-v-3"></div>
          </div>

          {/* Infinite Marquee Text */}
          <div className="marquee-wrapper select-none position-absolute w-100 scroll-anim-fade-in" style={{ top: '15%', opacity: 0.05 }}>
          </div>

          <div className="marquee-wrapper select-none position-absolute w-100 scroll-anim-fade-in">
            <div className="marquee-content text-white">
              <span>Amazon &nbsp; Web &nbsp; Services &nbsp; &amp; &nbsp;</span>
              <span>Enterprise Cloud Infrastructures </span>
            </div>
          </div>

          {/* Main Content Layout */}
          <div className="container position-relative z-3 my-5">
            <div className="row align-items-center py-4">

              {/* Left Side Info */}
              <div className="col-12 col-md-5 text-start d-flex flex-column align-items-start hero-left-pane text-white mb-4 mb-md-0 scroll-anim-fade-in-left">
                <div className="asterisk-icon mb-3 text-amber fs-3" style={{ color: '#ff9900' }}>✳</div>
                <h1 className="fw-bold mb-3 display-5">AWS Cloud Platforms</h1>
                <p className="tagline mb-4 text-white" style={{ fontSize: '1rem', lineHeight: '1.6', opacity: 0.9 }}>
                  At Affsoft IT Solutions Pvt. Ltd., we eliminate the performance bottlenecks that slow down distributed digital operations. Our AWS cloud engineering practice pairs robust multi-region container scaling with optimized serverless applications and clear infrastructure-as-code deployments to turn raw infrastructure into fast strategic spaces.
                </p>
                <button className="btn btn-outline-light d-flex align-items-center gap-2 px-4 py-2 rounded-pill fw-semibold">
                  <span>➔</span>
                  <span>Propose a Cloud Blueprint</span>
                </button>
              </div>

              {/* Center Section */}
              <div className="col-12 col-md-4 text-center mb-4 mb-md-0 hero-center-pane scroll-anim-flip">
                <div className="typing-badge px-4 py-3 mx-auto bg-dark bg-opacity-75 rounded-pill border border-secondary shadow-lg d-inline-block">
                  <span className="typed-text text-white fw-mono fs-4">{displayedText}</span>
                  <span className="cursor fs-4 animate-blink" style={{ color: '#ff9900' }}>|</span>
                </div>
              </div>

              {/* Right Section */}
              <div className="col-12 col-md-3 d-flex justify-content-center justify-content-md-end hero-right-pane scroll-anim-fade-in-right">
                <div className="glass-card-frame shadow-lg p-3 bg-white bg-opacity-10 rounded-4 border border-light" style={{ width: '220px', backdropFilter: 'blur(10px)' }}>
                  <div className={`inner-graphic-card graphic-${cardData[cardIndex].type} d-flex align-items-center justify-content-center rounded-3 bg-dark`} style={{ height: '180px' }}>
                    <span className="graphic-icon display-3">{cardData[cardIndex].content}</span>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* ================= CORE SOLUTIONS SECTION ================= */}
        <section className="py-5 bg-white">
          <div className="container">
            <div className="text-center max-width-700 mx-auto mb-5 scroll-anim-fade-in-up">
              <span className="badge px-3 py-2 rounded-pill mb-2 text-uppercase text-white" style={{ backgroundColor: '#ff9900' }}>Cloud Infrastructure Scaling</span>
              <h2 className="fw-bold text-dark display-6">Deploy Auto-Scaling Compute Pools & Resilient Infrastructure as Code</h2>
              <p className="text-muted mt-3">
                Unoptimized instance allocations, fragile configuration routines, and isolated runtime data structures can heavily impact business operations. At Affsoft IT Solutions Pvt. Ltd., we build secure, zero-downtime, and systematically tracked AWS cloud networks.
              </p>
            </div>

            <div className="row g-4">
              {coreServices.map((service, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-zoom-in" key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="card h-100 shadow-sm border-0 rounded-4 p-4 hover-translate transition-all">
                    <div className="fs-1 mb-3" style={{ color: '#ff9900' }}>{service.icon}</div>
                    <h4 className="fw-bold fs-5 mb-2 text-dark">{service.title}</h4>
                    <p className="text-muted mb-0 small">{service.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Business Value Checklist */}
            <div className="mt-5 p-4 rounded-4 bg-light border scroll-anim-fade-in-up">
              <h5 className="fw-bold text-center text-dark mb-4">Core Operational Gains of Strategic Enterprise AWS Modernization</h5>
              <div className="row g-3">
                {designBenefits.map((benefit, index) => (
                  <div className="col-6 col-md-3 d-flex align-items-center gap-2" key={index}>
                    <span className="text-success fs-5">✓</span>
                    <span className="text-secondary small fw-medium">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ================= ADVANCED SOLUTIONS OPTIONS ================= */}
        <section className="py-5 bg-light">
          <div className="container">
            <div className="text-center max-width-700 mx-auto mb-5 scroll-anim-fade-in-up">
              <span className="badge px-3 py-2 rounded-pill mb-2 text-uppercase text-white" style={{ backgroundColor: '#ff9900' }}>Distributed Cloud Nodes</span>
              <h2 className="fw-bold text-dark display-6">Adaptive Platform Capabilities Built for Massive Request Traffic</h2>
              <p className="text-muted mt-3">
                We scale standard single-server layouts into highly distributed, self-healing runtime systems. From deploying generative AI paths using Bedrock to optimizing high-volume global message buses, our configurations protect transaction durability.
              </p>
            </div>

            <div className="row g-4">
              {advancedServices.map((service, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-zoom-in" key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="card h-100 shadow-sm border-0 rounded-4 p-4 bg-white hover-translate transition-all">
                    <div className="fs-1 mb-3" style={{ color: '#ff9900' }}>{service.icon}</div>
                    <h4 className="fw-bold fs-5 mb-2 text-dark">{service.title}</h4>
                    <p className="text-muted mb-0 small">{service.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= TECHNOLOGIES SECTION ================= */}
        <section className="py-5 bg-dark text-white">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <span className="badge px-3 py-2 rounded-pill mb-2 text-uppercase fw-bold text-dark" style={{ backgroundColor: '#ff9900' }}>AWS Architecture Stack</span>
              <h2 className="fw-bold display-6">Infrastructure Systems & Toolsets We Leverage</h2>
              <p className="text-light-50">We configure, monitor, and safeguard enterprise operational environments utilizing strict validation matrices.</p>
            </div>

            <div className="row g-4 justify-content-center">
              {Object.entries(technologies).map(([category, list], index) => (
                <div className="col-12 col-sm-6 col-md-4 col-lg scroll-anim-fade-in" key={index}>
                  <div className="bg-secondary bg-opacity-25 p-4 rounded-4 h-100 border border-secondary border-opacity-50">
                    <h5 className="fw-bold mb-3 border-bottom border-secondary pb-2" style={{ color: '#ff9900' }}>{category}</h5>
                    <ul className="list-unstyled mb-0">
                      {list.map((tech, i) => (
                        <li key={i} className="mb-2 d-flex align-items-center gap-2 small">
                          <span className="opacity-75" style={{ color: '#ff9900' }}>•</span> {tech}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= ROADMAP PROCESS ================= */}
        <section className="py-5 bg-white">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-secondary px-3 py-2 rounded-pill mb-2 text-uppercase">Migration Lifecycle</span>
              <h2 className="fw-bold text-dark display-6">Our Engineered Cloud Deployment Blueprint</h2>
              <p className="text-muted">A structured, strictly governed development lifecycle designed to transition heavy core processes safely from staging phases onto active live production instances.</p>
            </div>

            <div className="row g-3">
              {processSteps.map((step, index) => (
                <div className="col-12 col-md-6 col-lg scroll-anim-fade-in-up" key={index} style={{ animationDelay: `${index * 0.15}s` }}>
                  <div className="p-4 rounded-4 bg-light border-start border-4 h-100 shadow-sm" style={{ borderLeftColor: '#ff9900' }}>
                    <div className="fw-bold display-6 mb-2" style={{ color: '#ff9900' }}>{step.num}</div>
                    <h5 className="fw-bold text-dark fs-6">{step.title}</h5>
                    <p className="text-muted small mb-0">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= INDUSTRIES SERVED ================= */}
        <section className="py-5 bg-light">
          <div className="container text-center">
            <h2 className="fw-bold text-dark mb-4 display-6 scroll-anim-fade-in-up">Ecosystems We Empower</h2>
            <div className="d-flex flex-wrap justify-content-center gap-2 max-width-900 mx-auto scroll-anim-zoom-in">
              {industries.map((industry, index) => (
                <span className="badge bg-white text-dark border px-3 py-2 rounded-pill shadow-sm fs-7 fw-medium" key={index}>
                  🏢 {industry}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* ================= WHY CHOOSE AFFSOFT ================= */}
        <section className="py-5 bg-white border-top">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <h2 className="fw-bold text-dark display-6">Why Partner with Affsoft IT Solutions Pvt. Ltd.?</h2>
              <p className="text-muted">We combine programmatic software mastery with robust deployment pipeline automation to guarantee stable enterprise software behavior.</p>
            </div>

            <div className="row g-3">
              {whyChooseUs.map((reason, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-fade-in-left" key={index} style={{ animationDelay: `${(index % 3) * 0.1}s` }}>
                  <div className="d-flex align-items-center gap-3 p-3 bg-light rounded-3 h-100">
                    <span className="fs-4" style={{ color: '#ff9900' }}>🛡️</span>
                    <span className="text-dark fw-semibold small">{reason}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Custom Styles Injection */}
        <style>{`
        .hover-translate {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .hover-translate:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.1) !important;
        }
        .text-light-50 {
          color: rgba(255, 255, 255, 0.7);
        }
        .max-width-700 {
          max-width: 700px;
        }
        .max-width-900 {
          max-width: 900px;
        }
        .animate-blink {
          animation: blink 1s step-end infinite;
        }
        @keyframes blink {
          from, to { color: transparent }
          50% { color: inherit }
        }

        /* --- Scroll Animation Core --- */
        .scroll-anim-fade-in,
        .scroll-anim-fade-in-up,
        .scroll-anim-fade-in-left,
        .scroll-anim-fade-in-right,
        .scroll-anim-flip,
        .scroll-anim-zoom-in {
          opacity: 0;
          will-change: transform, opacity;
          transition: all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }

        /* Visibility Trigger via IntersectionObserver */
        .animate-visible {
          opacity: 1 !important;
          transform: none !important;
        }

        /* --- Animations Configurations --- */
        .scroll-anim-fade-in-up {
          transform: translateY(40px);
        }
        .scroll-anim-fade-in-left {
          transform: translateX(-50px);
        }
        .scroll-anim-fade-in-right {
          transform: translateX(50px);
        }
        .scroll-anim-zoom-in {
          transform: scale(0.85);
        }
        .scroll-anim-flip {
          transform: perspective(400px) rotateY(90deg);
          transition: all 1s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        }
        
        .scroll-anim-flip.animate-visible {
          transform: perspective(400px) rotateY(0deg) !important;
        }
      `}</style>

      </div>

    </>
  );
};

export default AWSCloudSolutions;